import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faFileUpload, faInfoCircle, faChartBar } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { rescrapWebsite, uploadDocuments, getCrawlStatus } from '../services/api';
import CrawlStatusMonitor from '../components/CrawlStatusMonitor';
import { addCrawlJob, getActiveCrawlJobs } from '../services/crawlStatusService';
import { setCrawlingInitiated } from '../components/CrawlStatusIcon';

interface Props {
    clientId?: string | null;
}

type TabType = 'upload' | 'rescrap';

const UploadForm: React.FC<Props> = ({ clientId: propClientId }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<TabType>('upload');
    const [url, setUrl] = useState('');
    const [isRescrapLoading, setIsRescrapLoading] = useState(false);
    const [isUploadLoading, setIsUploadLoading] = useState(false);
    const [clientId, setClientId] = useState<string | null>(propClientId || localStorage.getItem('client_id'));
    const [crawlJobId, setCrawlJobId] = useState<string | null>(null);
    const [showCrawlStatus, setShowCrawlStatus] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Effect to update clientId if localStorage or props change
    useEffect(() => {
        console.log('UploadForm useEffect clientId:', propClientId);
        if (!propClientId) {
            const storedClientId = localStorage.getItem('client_id');
            if (storedClientId !== clientId) {
                setClientId(storedClientId);
                console.log('Using client ID from localStorage:', storedClientId);
            }
        } else if (propClientId !== clientId) {
            setClientId(propClientId);
        }
    }, [propClientId, clientId]);

    // Check if a crawl is already running when component mounts
    useEffect(() => {
        const checkActiveCrawl = async () => {
            if (!clientId) return;
            
            try {
                const crawlStatus = await getCrawlStatus(clientId);
                if (crawlStatus && crawlStatus.status && 
                    ['initializing', 'in_progress', 'embedding'].includes(crawlStatus.status)) {
                    toast.info('A crawl is already in progress. Redirecting to crawl details...');
                    setTimeout(() => {
                        navigate(`/crawl-details/${clientId}`);
                    }, 1000);
                }
            } catch (error) {
                console.error('Error checking crawl status:', error);
            }
        };

        checkActiveCrawl();
    }, [clientId, navigate]);

    const handleRescrap = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!clientId || !url.trim()) {
            toast.error('Please enter a valid URL');
            return;
        }

        setIsRescrapLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            // Check if a crawl is already running
            const crawlStatus = await getCrawlStatus(clientId);
            if (crawlStatus && crawlStatus.status && 
                ['initializing', 'in_progress', 'embedding'].includes(crawlStatus.status)) {
                toast.info('A crawl is already in progress. Redirecting to crawl details...');
                navigate(`/crawl-details/${clientId}`);
                return;
            }
            
            // Signal that crawling has been initiated
            setCrawlingInitiated(true);
            
            // Check if we already have active crawl jobs
            const activeJobs = getActiveCrawlJobs().filter(job => !job.hidden);
            const shouldCreateLocalJob = activeJobs.length === 0;
            
            // Create a unique frontend job ID 
            const localJobId = `frontend-${Date.now()}-${clientId}`;
            
            if (shouldCreateLocalJob) {
                // Create a placeholder job before starting the crawl
                console.log(`[UPLOAD] Creating placeholder job with ID ${localJobId}`);
                
                addCrawlJob({
                    id: localJobId,
                    clientId: clientId,
                    url: url,
                    status: 'initializing',
                    pages_crawled: 0,  // Note: This will be updated from SSE with pages_scraped/pages_processed
                    pages_in_queue: 1  // Start with 1 (the initial URL)
                });
            }
            
            // Start the crawl first
            const result = await rescrapWebsite(clientId, url, undefined, true);
            toast.success('Website crawling started successfully');
            
            // Clear the URL field
            setUrl('');
            
            // Then navigate to details page to show progress
            navigate(`/crawl-details/${clientId}`, { 
                state: { fromRescrap: true }
            });
        } catch (error: any) {
            console.error('Rescrap error:', error);
            if (error.response?.status === 409) {
                toast.warning('A crawl is already in progress. Redirecting to crawl details...');
                navigate(`/crawl-details/${clientId}`);
            } else {
                toast.error('Failed to start crawling');
            }
        } finally {
            setIsRescrapLoading(false);
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!clientId || !files?.length) {
            return;
        }

        setIsUploadLoading(true);
        try {
            const formData = new FormData();
            Array.from(files).forEach(file => {
                formData.append('documents', file);
            });
            formData.append('client_id', clientId);

            await uploadDocuments(formData);
            toast.success('Documents uploaded successfully');
            // Reset file input
            e.target.value = '';
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Failed to upload documents');
        } finally {
            setIsUploadLoading(false);
        }
    };

    const handleCrawlComplete = (success: boolean) => {
        if (success) {
            toast.success('Website crawling and embedding completed successfully');
        } else {
            toast.error('Website crawling completed with errors');
        }
    };

    const TabButton: React.FC<{ tab: TabType; label: string }> = ({ tab, label }) => (
        <button
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 px-6 text-sm font-medium rounded-lg transition-colors duration-200
                ${activeTab === tab 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
            {label}
        </button>
    );

    return (
        <div>
            <h1 className="text-2xl font-bold mb-8">
                {activeTab === 'upload' ? 'Upload Documents' : 'Website Crawler'}
            </h1>

            {/* Tabs */}
            <div className="flex space-x-4 mb-8">
                <TabButton tab="upload" label="Upload Additional Documents" />
                <TabButton tab="rescrap" label="Re-crawl Website" />
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-[30px] p-8 shadow-sm">
                {activeTab === 'upload' ? (
                    <div className="text-center">
                        <FontAwesomeIcon icon={faFileUpload} className="text-blue-500 text-3xl mb-4" />
                        <h2 className="text-xl font-semibold mb-4">Upload Additional Documents</h2>
                        <p className="text-gray-600 mb-6">
                            Upload PDF documents or other supported files to enhance your knowledge base.
                            These documents will be added to your existing knowledge base.
                        </p>
                        
                        <div className="max-w-md mx-auto">
                            <label htmlFor="file-upload" className="flex flex-col items-center justify-center w-full h-32 border-2 border-blue-300 border-dashed rounded-lg cursor-pointer bg-blue-50 hover:bg-blue-100">
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <FontAwesomeIcon icon={faFileUpload} className="text-blue-500 text-2xl mb-2" />
                                    <p className="mb-2 text-sm text-blue-700">
                                        <span className="font-semibold">Click to upload</span> or drag and drop
                                    </p>
                                    <p className="text-xs text-blue-500">
                                        PDF, DOC, DOCX, TXT (MAX. 10MB)
                                    </p>
                                </div>
                                <input 
                                    id="file-upload" 
                                    type="file" 
                                    className="hidden" 
                                    onChange={handleFileChange}
                                    multiple
                                    accept=".pdf,.doc,.docx,.txt"
                                    disabled={isUploadLoading}
                                />
                            </label>
                        </div>
                    </div>
                ) : (
                    <div className="text-center">
                        <FontAwesomeIcon icon={faSync} className="text-blue-500 text-3xl mb-4" />
                        <h2 className="text-xl font-semibold mb-4">Re-crawl Website</h2>
                        
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 text-left mb-6 rounded">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mt-1" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-blue-700 font-medium">Important Information</p>
                                    <ul className="mt-1 text-sm text-blue-700 list-disc list-inside">
                                        <li>This will delete existing scraped content and embeddings, then crawl the website to get fresh data.</li>
                                        <li>If the URL is different from the source one, the existing content will be replaced with the new website data.</li>
                                        <li>The crawler will automatically crawl all available pages.</li>
                                        <li>This action cannot be undone.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        
                        <form onSubmit={handleRescrap} className="max-w-md mx-auto space-y-4">
                            <div>
                                <label htmlFor="website-url" className="block text-left text-sm font-medium text-gray-700 mb-1">
                                    Website URL
                                </label>
                                <input
                                    id="website-url"
                                    type="url"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="Enter website URL to crawl"
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            
                            <button
                                type="submit"
                                disabled={isRescrapLoading || !url.trim()}
                                className={`
                                    w-full px-4 py-2 rounded-md text-white relative
                                    ${(isRescrapLoading || !url.trim())
                                        ? 'bg-blue-400 cursor-not-allowed'
                                        : 'bg-blue-600 hover:bg-blue-700'}
                                `}
                            >
                                {isRescrapLoading ? (
                                    <div className="flex items-center justify-center">
                                        <FontAwesomeIcon icon={faSync} spin className="mr-2" />
                                        <span>Processing...</span>
                                    </div>
                                ) : (
                                    'Start Crawling'
                                )}
                            </button>
                        </form>
                        
                        {/* Link to Crawl Results */}
                        {clientId && (
                            <div className="mt-8 pt-6 border-t border-gray-200">
                                <Link 
                                    to={`/crawl-details/${clientId}`}
                                    className="inline-flex items-center text-blue-600 hover:text-blue-800"
                                >
                                    <FontAwesomeIcon icon={faChartBar} className="mr-2" />
                                    View Crawling Results & Statistics
                                </Link>
                                <p className="text-xs text-gray-500 mt-1">
                                    View details about current and past crawling operations
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            {/* Crawl Status Monitor - Removed to prevent duplicate with PersistentCrawlMonitor */}
            {/* {showCrawlStatus && clientId && (
                <CrawlStatusMonitor 
                    clientId={clientId} 
                    jobId={crawlJobId} 
                    onComplete={handleCrawlComplete}
                />
            )} */}
        </div>
    );
};

export default UploadForm; 